import React, { useEffect } from 'react';
import { HandHeart, Lock, Zap } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { getCurrentLanguage } from '../utils/translations';

const DonPage = () => {
  const currentLanguage = getCurrentLanguage();
  
  // Charger le script Donorbox
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://donorbox.org/widget.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const content = {
    fr: {
      title: 'FAITES UN DON',
      subtitle: "Devenez acteur clé de la lutte contre l'extrême pauvreté au Bénin"
    },
    en: {
      title: 'MAKE A DONATION',
      subtitle: 'Become a key player in the fight against extreme poverty in Benin'
    }
  };

  const t = (key) => content[currentLanguage === 'en' ? 'en' : 'fr'][key];

  const impactItems = [
    { n: 1, fr: 'Cadre de vie',                  en: 'Living conditions',        icon: '🏘️' },
    { n: 2, fr: 'Eau et assainissement',         en: 'Water and sanitation',     icon: '💧' },
    { n: 3, fr: 'Santé et sécurité alimentaire', en: 'Health and food security', icon: '🏥' },
    { n: 4, fr: 'Sensibilisation et éducation', en: 'Awareness and education',  icon: '📚' },
    { n: 5, fr: "Protection de l'environnement", en: 'Environmental protection', icon: '🌿' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero */}
      <div className="relative bg-gradient-to-r from-[#089556] via-[#0a7c4a] to-[#089556] py-16">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <HandHeart className="h-16 w-16 text-white/80 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight drop-shadow-2xl mb-4">
            {t('title')}
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            {t('subtitle')}
          </p>
        </div>
      </div>

      {/* Main Content - Donorbox Widget */}
      <main className="max-w-6xl mx-auto px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 items-start">

          {/* Left Column - Impact Info */}
          <div className="space-y-5">

            {/* Impact Card */}
            <div className="bg-white rounded-2xl shadow-md p-6 border border-[#07D1EB]/20">
              <h2 className="text-xl font-bold text-[#089556] mb-5 flex items-center gap-2">
                <Zap className="h-6 w-6 text-[#07D1EB]" />
                {currentLanguage === 'fr' ? 'Votre Don Soutient' : 'Your Donation Supports'}
              </h2>
              <div className="space-y-2">
                {impactItems.map(item => (
                  <div key={item.n} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                    <span className="text-2xl w-8 text-center">{item.icon}</span>
                    <span className="font-medium text-gray-800">
                      {currentLanguage === 'fr' ? item.fr : item.en}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Security + Impact row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                <Lock className="h-5 w-5 text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-800 text-sm mb-1">
                  {currentLanguage === 'fr' ? 'Paiement Sécurisé' : 'Secure Payment'}
                </h3>
                <p className="text-xs text-gray-500">
                  {currentLanguage === 'fr' ? 'Crypté par Donorbox' : 'Encrypted by Donorbox'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <HandHeart className="h-5 w-5 text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-800 text-sm mb-1">
                  {currentLanguage === 'fr' ? 'Impact Réel' : 'Real Impact'}
                </h3>
                <p className="text-xs text-gray-500">
                  15 000+ {currentLanguage === 'fr' ? 'bénéficiaires' : 'beneficiaries'}
                </p>
              </div>
            </div>

            {/* Warning */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-xs text-gray-700">
                ⚠️ <strong>Important :</strong>{' '}
                {currentLanguage === 'fr'
                  ? 'AHB ne demandera jamais vos informations bancaires par téléphone ou SMS.'
                  : 'AHB will never ask for your banking information by phone or SMS.'}
              </p>
            </div>
          </div>

          {/* Right Column - Donorbox Widget */}
          <div>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-4">
              <iframe 
                src="https://donorbox.org/embed/action-humanitaire-benin?default_interval=o" 
                name="donorbox" 
                allowpaymentrequest="allowpaymentrequest" 
                seamless="seamless" 
                frameBorder="0" 
                scrolling="no" 
                height="900px" 
                width="100%" 
                style={{
                  maxWidth: '100%',
                  minWidth: '100%',
                  maxHeight: 'none',
                  border: 'none'
                }}
                allow="payment"
                title="Donorbox Donation Form"
              ></iframe>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DonPage;

/* ===== FORMULAIRE KOMIPAY - COMMENTÉ POUR RÉFÉRENCE FUTURE =====

import React, { useState } from 'react';
import { CreditCard, ShieldCheck, HandHeart, Loader2, CheckCircle, XCircle, Lock, Zap } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import api from '../components/api';
import { getCurrentLanguage } from '../utils/translations';

const DonPage = () => {
  const currentLanguage = getCurrentLanguage();
  const [formData, setFormData] = useState({ name: '', card_number: '', expiration: '', cvv: '', amount: '' });
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currency, setCurrency] = useState('XOF');

  const RATES = { XOF: 1, EUR: 655.957, USD: 600 };
  const CURRENCY_SYMBOLS = { XOF: 'FCFA', EUR: '€', USD: '$' };
  const MIN_AMOUNTS = { XOF: 100, EUR: 0.15, USD: 0.17 };

  const toFCFA = (amount, cur) => Math.round(parseFloat(amount) * RATES[cur]);

  const content = {
    fr: {
      title: 'FAITES UN DON',
      subtitle: "Devenez acteur clé de la lutte contre l'extrême pauvreté au Bénin",
      cardNumber: 'Numéro de carte',
      expirationDate: "Date d'expiration",
      amount: 'Montant',
      donorName: 'Nom complet',
      securePayment: 'Don Sécurisé',
      processPayment: 'Effectuer un don sécurisé',
      processing: 'Traitement en cours...',
      paymentSuccess: 'Don effectué avec succès !',
      paymentError: 'Erreur lors du paiement',
      tryAgain: 'Réessayer',
      newPayment: 'Nouveau paiement'
    },
    en: {
      title: 'MAKE A DONATION',
      subtitle: 'Become a key player in the fight against extreme poverty in Benin',
      cardNumber: 'Card number',
      expirationDate: 'Expiration date',
      amount: 'Amount',
      donorName: 'Full name',
      securePayment: 'Secure Donation',
      processPayment: 'Make a secure donation',
      processing: 'Processing...',
      paymentSuccess: 'Donation successful!',
      paymentError: 'Payment error',
      tryAgain: 'Try again',
      newPayment: 'New payment'
    }
  };

  const t = (key) => content[currentLanguage === 'en' ? 'en' : 'fr'][key];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'card_number') {
      const clean = value.replace(/\s/g, '');
      setFormData({ ...formData, [name]: clean.match(/.{1,4}/g)?.join(' ') || clean });
    } else if (name === 'expiration') {
      const clean = value.replace(/\D/g, '');
      setFormData({ ...formData, [name]: clean.length >= 2 ? clean.slice(0, 2) + '/' + clean.slice(2, 4) : clean });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPaymentStatus(null);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const amountInFCFA = currency === 'XOF' ? formData.amount : toFCFA(formData.amount, currency);
      const paymentData = {
        name: formData.name,
        card_number: formData.card_number.replace(/\s/g, ''),
        expiration: formData.expiration,
        cvv: formData.cvv,
        amount: amountInFCFA,
        reference: 'DON-' + Date.now()
      };

      const response = await api.post('/api/payment/card/', paymentData);
      const data = response.data;

      if (response.status === 200 && data?.statut === true) {
        if (data.etat === 'SUCCESS') {
          setPaymentStatus('success');
          setSuccessMessage(t('paymentSuccess'));
          setFormData({ name: '', card_number: '', expiration: '', cvv: '', amount: '' });
        } else if (data.etat === 'ATTENTE') {
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
          } else {
            setPaymentStatus('error');
            setErrorMessage('Lien de validation 3DS manquant. Veuillez réessayer.');
          }
        } else {
          setPaymentStatus('error');
          setErrorMessage(data.message || t('paymentError'));
        }
      } else {
        setPaymentStatus('error');
        setErrorMessage(data?.error || data?.message || t('paymentError'));
      }
    } catch (error) {
      setPaymentStatus('error');
      let errorMsg = 'Une erreur est survenue lors du traitement du paiement';
      if (error.response?.data) {
        const data = error.response.data;
        if (data.error) {
          errorMsg = data.error;
          if (errorMsg.includes('token') || errorMsg.includes('Token')) errorMsg = "Erreur d'authentification. Veuillez réessayer.";
          else if (errorMsg.includes('CVV') || errorMsg.includes('cvv')) errorMsg = 'Code CVV incorrect.';
          else if (errorMsg.includes('expir')) errorMsg = 'Carte expirée.';
          else if (errorMsg.includes('Solde') || errorMsg.includes('insuffisant')) errorMsg = 'Fonds insuffisants.';
        } else if (data.message) {
          errorMsg = data.message;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetPayment = () => {
    setPaymentStatus(null);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const impactItems = [
    { n: 1, fr: 'Cadre de vie',                  en: 'Living conditions',        icon: '🏘️' },
    { n: 2, fr: 'Eau et assainissement',         en: 'Water and sanitation',     icon: '💧' },
    { n: 3, fr: 'Santé et sécurité alimentaire', en: 'Health and food security', icon: '🏥' },
    { n: 4, fr: 'Sensibilisation et éducation', en: 'Awareness and education',  icon: '📚' },
    { n: 5, fr: "Protection de l'environnement", en: 'Environmental protection', icon: '🌿' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="relative bg-gradient-to-r from-[#089556] via-[#0a7c4a] to-[#089556] py-16">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <HandHeart className="h-16 w-16 text-white/80 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight drop-shadow-2xl mb-4">
            {t('title')}
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            {t('subtitle')}
          </p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          [... reste du code avec formulaire Komipay ...]
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DonPage;

===== FIN FORMULAIRE KOMIPAY ===== */
